// Turning a club's five configured colours into the dozen CSS custom
// properties the app actually paints with.
//
// Its own module rather than part of ConfigContext because this is pure colour
// arithmetic with no React in it: it can be read, reasoned about and tested on
// its own, which the effect that injects the result cannot.
//
// WHY DERIVE AT ALL. A club sets five colours; the stylesheets use twelve. The
// missing seven used to fall back to css/global.css's defaults, so a client
// could set its own text and background and still get the stock palette's
// borders, secondary text and white cards sitting on top of them — the theme
// applied to about a third of the screen. The relationships are what make a
// palette hold together (a border is the text colour at low alpha; a card is
// one step away from the page), and asking a club to get twelve colours
// consistent by hand is asking it to do this arithmetic itself.

const HEX_COLOR_RE = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

/**
 * Whether a configured colour is a hex value this file can safely inject.
 * Checked rather than trusted: it is written straight into a stylesheet.
 *
 * @param {*} value
 * @returns {boolean}
 */
export const isValidHex = (value) => typeof value === 'string' && HEX_COLOR_RE.test(value.trim());

/**
 * Splits a hex colour into its channels.
 * @param {string} hex - '#rgb' or '#rrggbb'.
 * @returns {{r: number, g: number, b: number}}
 */
export const hexToRgb = (hex) => {
  const clean = hex.trim().replace('#', '');
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  const num = parseInt(full, 16);
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
};

/**
 * The same colour at a given alpha, as an rgba() string.
 * @param {string} hex
 * @param {number} alpha - 0-1.
 * @returns {string}
 */
export const hexToRgba = (hex, alpha) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/** Two channel values to a #rrggbb string. @param {{r,g,b}} rgb @returns {string} */
export const rgbToHex = ({ r, g, b }) => {
  const toHex = (channel) => Math.max(0, Math.min(255, Math.round(channel))).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Lightens (positive) or darkens (negative) a hex colour, for hover and
 * pressed states that follow the theme instead of a hardcoded tint.
 *
 * @param {string} hex
 * @param {number} percent - e.g. -12 for 12% darker.
 * @returns {string}
 */
export const shadeHex = (hex, percent) => {
  const { r, g, b } = hexToRgb(hex);
  const adjust = (channel) => channel + (channel * (percent / 100));
  return rgbToHex({ r: adjust(r), g: adjust(g), b: adjust(b) });
};

/**
 * A colour some fraction of the way from one to another.
 *
 * Used instead of shadeHex wherever the result has to work on a DARK theme
 * too: shading multiplies, so it cannot lighten black — 0 times anything is
 * still 0 — while mixing toward white always can.
 *
 * @param {string} hex
 * @param {string} towards
 * @param {number} amount - 0 = unchanged, 1 = fully `towards`.
 * @returns {string}
 */
export const mixHex = (hex, towards, amount) => {
  const from = hexToRgb(hex);
  const to = hexToRgb(towards);
  return rgbToHex({
    r: from.r + (to.r - from.r) * amount,
    g: from.g + (to.g - from.g) * amount,
    b: from.b + (to.b - from.b) * amount,
  });
};

/**
 * How bright a colour reads, 0 (black) to 1 (white).
 *
 * The WCAG relative-luminance formula rather than a plain channel average,
 * because the eye is far more sensitive to green than to blue: averaged,
 * pure blue and pure yellow come out equally "bright", and text placed on
 * them by that measure is unreadable on one of the two.
 *
 * @param {string} hex
 * @returns {number}
 */
export const luminance = (hex) => {
  const { r, g, b } = hexToRgb(hex);
  const channel = (value) => {
    const scaled = value / 255;
    return scaled <= 0.03928 ? scaled / 12.92 : ((scaled + 0.055) / 1.055) ** 2.4;
  };
  return (0.2126 * channel(r)) + (0.7152 * channel(g)) + (0.0722 * channel(b));
};

/**
 * The contrast ratio between two colours, 1 (identical) to 21 (black on white).
 *
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
export const contrastRatio = (a, b) => {
  const first = luminance(a);
  const second = luminance(b);
  const lighter = Math.max(first, second);
  const darker = Math.min(first, second);
  return (lighter + 0.05) / (darker + 0.05);
};

/** Near-black rather than pure black: softer on a light accent, same contrast. */
const ON_DARK = '#ffffff';
const ON_LIGHT = '#1a1a1a';

/**
 * Black or white, whichever can actually be READ on top of this colour.
 *
 * Measured, not guessed from a brightness threshold. A threshold picks by how
 * light a colour looks; this picks by how much contrast each option really
 * gives, which is the thing that decides whether a label can be read. The two
 * disagree in the middle of the range — a muted teal or a mid grey — and the
 * middle is where buttons most often land.
 *
 * What it fixes: the label on a primary button was white whatever colour the
 * button was, so a club that chose a pale accent got white-on-pale and a till
 * nobody could read.
 *
 * @param {string} hex
 * @returns {string}
 */
export const readableOn = (hex) => (
  contrastRatio(hex, ON_DARK) >= contrastRatio(hex, ON_LIGHT) ? ON_DARK : ON_LIGHT
);

/**
 * Light grey — the line colour when the configured text colour cannot draw one
 * of its own. On a dark card, white at low alpha does the same job in the
 * other direction.
 */
const NEUTRAL_LINE_LIGHT = '#c9c9c9';
const NEUTRAL_LINE_DARK = '#ffffff';

/**
 * The least difference a border needs from the card it outlines before it is a
 * border rather than a rumour. 1.6:1 is well below text contrast — a hairline
 * only has to be noticed, not read.
 */
const LINE_MIN_CONTRAST = 1.6;

/**
 * The colour every border is tinted with.
 *
 * TAKEN FROM THE TEXT COLOUR, so a hairline is the same ink as the copy it
 * frames, just faint. That is also the one colour guaranteed to stand out from
 * the page: it has to, or nothing on the screen could be read.
 *
 * The fallback is a backstop rather than the normal path. A text colour that
 * cannot be seen against its own card would be an unreadable theme anyway, but
 * the border should not be the thing that disappears first, so anything below
 * the contrast floor gives way to whichever neutral works in that direction.
 *
 * @param {string} textColor - Theme -> Text Color.
 * @param {string} surface - the card colour the line will sit on.
 * @returns {string} a hex colour to tint at low alpha.
 */
export const deriveLineTint = (textColor, surface) => {
  if (contrastRatio(textColor, surface) >= LINE_MIN_CONTRAST) return textColor;
  return luminance(surface) > 0.5 ? NEUTRAL_LINE_LIGHT : NEUTRAL_LINE_DARK;
};

/**
 * The five colours a club actually configures, and what the app falls back to.
 *
 * Exported so ConfigContext and anything testing this agree on one set of
 * defaults rather than each carrying its own copy.
 */
export const DEFAULT_THEME = {
  themeTextColor: '#3d1220',
  themeBackgroundColor: '#f1efe9',
  themeHeaderBackgroundColor: '#ffffff',
  themeActiveColor: '#6fb5ab',
  themeBrandColor: '#3d1220',
};

/**
 * Colours a club may leave blank, and what blank means.
 *
 * Only the active-button text so far. It is absent from DEFAULT_THEME on
 * purpose: its default is not a colour, it is a calculation — see
 * resolveActiveTextColor — and putting a fixed value here would freeze the
 * answer for a button whose colour can change afterwards.
 */
export const OPTIONAL_THEME_KEYS = ['themeActiveTextColor'];

/**
 * The colour to write on the active/primary fill.
 *
 * A club's own choice when it made one, and otherwise black or white by
 * measured contrast against the button they will actually sit on — which is
 * the SHADED active colour, not the raw one, since that is what the button is
 * painted with.
 *
 * Exported because the Config screen shows the same answer in its swatch while
 * the field is blank; computing it twice is how the preview and the app end up
 * disagreeing.
 *
 * @param {object} [theme]
 * @returns {string}
 */
export const resolveActiveTextColor = (theme = {}) => {
  if (isValidHex(theme.themeActiveTextColor)) return theme.themeActiveTextColor.trim();
  const active = isValidHex(theme.themeActiveColor)
    ? theme.themeActiveColor.trim()
    : DEFAULT_THEME.themeActiveColor;
  return readableOn(shadeHex(active, -12));
};

/**
 * The :root block for one client's theme.
 *
 * Every value is either configured or derived from something configured —
 * nothing here falls through to the stylesheet's own defaults, which is the
 * whole point: a club that sets a dark background gets dark cards, borders
 * that show against them, and secondary text that is still readable, rather
 * than the stock palette's white cards sitting on top of its background.
 *
 * Invalid or missing colours fall back to DEFAULT_THEME one at a time, so one
 * bad field costs that field and not the whole palette. Values are validated
 * before they are interpolated, because this string becomes a stylesheet.
 *
 * @param {object} [theme] - config, keyed by the DEFAULT_THEME keys.
 * @returns {string} the contents of a <style> tag.
 */
export const buildThemeCss = (theme = {}) => {
  const pick = (key) => (isValidHex(theme[key]) ? theme[key].trim() : DEFAULT_THEME[key]);

  const textColor = pick('themeTextColor');
  const bgColor = pick('themeBackgroundColor');
  const headerBg = pick('themeHeaderBackgroundColor');
  const activeColor = pick('themeActiveColor');
  const brandColor = pick('themeBrandColor');

  const inkRgb = hexToRgb(textColor);
  const bgRgb = hexToRgb(bgColor);
  const activeRgb = hexToRgb(activeColor);
  const deepColor = shadeHex(activeColor, -12);

  // A card lifts AWAY from the page, whichever direction that is: white on a
  // light background, a step lighter than the page on a dark one. Shading
  // alone cannot do the second — it multiplies, and nothing times zero
  // lightens black.
  const surface = luminance(bgColor) > 0.5 ? '#ffffff' : mixHex(bgColor, '#ffffff', 0.1);

  // Passed the colours as configured, so a club that set this field keeps its
  // choice and one that did not gets the calculation.
  const activeText = resolveActiveTextColor({ themeActiveColor: activeColor, themeActiveTextColor: theme.themeActiveTextColor });
  const activeTextRgb = hexToRgb(activeText);

  const lineTint = deriveLineTint(textColor, surface);
  const lineRgb = hexToRgb(lineTint);

  return `:root {
  --jt-ink: ${textColor};
  /* Raw channels so a stylesheet can build any alpha it needs off the
     configured colour — rgba(var(--jt-ink-rgb), .06) — instead of hardcoding
     a tint that then ignores the theme. */
  --jt-ink-rgb: ${inkRgb.r}, ${inkRgb.g}, ${inkRgb.b};
  /* Secondary and tertiary text: the text colour walked toward the page, so it
     stays legible on a dark theme instead of fading to a grey that was only
     ever chosen against a cream background. */
  --jt-ink-soft: ${mixHex(textColor, bgColor, 0.3)};
  --jt-ink-faint: ${mixHex(textColor, bgColor, 0.55)};

  --jt-bg: ${bgColor};
  --jt-bg-rgb: ${bgRgb.r}, ${bgRgb.g}, ${bgRgb.b};
  --jt-surface: ${surface};
  /* Borders follow the TEXT COLOUR — see deriveLineTint. --jt-line is the
     visible frame around a card; --jt-line-2 is the quieter rule used to
     divide one from the next. */
  --jt-line: ${hexToRgba(lineTint, 0.32)};
  --jt-line-2: ${hexToRgba(lineTint, 0.18)};
  --jt-line-rgb: ${lineRgb.r}, ${lineRgb.g}, ${lineRgb.b};

  --jt-header-bg: ${headerBg};
  --jt-brand: ${brandColor};

  /* What can be read on a block filled with the TEXT colour — the welcome
     page's closing strip does that. It was white, which disappears the moment
     a club picks a light text colour. */
  --jt-on-ink: ${readableOn(textColor)};

  --jt-cyan: ${activeColor};
  --jt-cyan-rgb: ${activeRgb.r}, ${activeRgb.g}, ${activeRgb.b};
  --jt-cyan-deep: ${deepColor};
  --jt-cyan-soft: ${hexToRgba(activeColor, 0.15)};
  /* What to write ON the active colour: the club's own choice, or measured
     contrast when it left the field blank — so a pale accent gets dark labels
     rather than the white ones that used to be hardcoded everywhere. */
  --jt-on-active: ${activeText};
  --jt-on-active-rgb: ${activeTextRgb.r}, ${activeTextRgb.g}, ${activeTextRgb.b};

  /* NOT themed, deliberately. Red means the same thing at every till, and a
     club that themed its errors to match its logo would be a club whose staff
     stop noticing them. Declared here only so the stylesheets can stop
     hardcoding the values. */
  --jt-danger: #c0392b;
  --jt-danger-rgb: 192, 57, 43;
  --jt-success: #2c7a52;
  --jt-amber: #b3730a;
}`;
};
